import {
  EmailSignatureBannerId,
  EmailSignatureContactDetailsFieldTypes,
  EmailSignatureCrop,
  EmailSignatureImage,
  EmailSignatureImageFieldId,
} from './../components/EmailSignaturev2/types';
import firebase from '@modules/firebase';

// utils
import {
  extractEmailSignatureContactDetailsFieldsFromCard,
  extractEmailSignaturePersonalDetailsFromCard,
  extractEmailSignatureSocialLinksFromCard,
  generateNewEmailSignatureContactFieldId,
  generateNewImageId,
  measureTextWidth,
} from '../modules/email-signatures-helpers';
import produce, { current, Draft } from 'immer';
import { contactDetailsFieldExceedsLimit } from '@modules/email-signatures-helpers';
import { pickBy } from 'lodash';

// types
import {
  EmailSignatureContactDetailsField,
  EmailSignatureSocialLink,
  EmailSignatureIconName,
  EmailSignatureFillStyle,
  EmailSignatureFonts,
  EmailSignatureShapes,
  EmailSignatureSizes,
  EmailSignatureSocialColors,
  EmailSignatureContactDetailsFieldId,
  EmailSignatureSocialLinkId,
  EmailSignatureV2,
  EmailSignatureLegalDisclaimer,
  EmailSignatureEnvironmentalFooter,
} from '@components/EmailSignaturev2/types';

import { EmailSignatureContactDetailsFieldWidth } from '@components/EmailSignaturev2/consts';

import { Card } from 'types';
import { deriveFieldWidth } from '@components/EmailSignaturev2/OnboardingFlow/mobile/details/utils';
import { TrackingPixel } from '.cache/__types__';

export const initialEmailSignatureDesignConfig = {
  font: EmailSignatureFonts.Helvetica,
  themeColor: '#f45a57',
  contactDetailsDisplayIcon: true,
  contactDetailsIconUseThemeColor: true,
  contactDetailsIconColor: '#f45a57',
  socialLinksFillStyle: EmailSignatureFillStyle.FULL,
  socialLinksShape: EmailSignatureShapes.CIRCLE,
  socialLinksSize: EmailSignatureSizes.MEDIUM,
  socialLinksColorMode: EmailSignatureSocialColors.BRAND,
  socialLinksColor: '#f45a57',
};

export const initialEmailSignatureContextValues: EmailSignatureV2 = {
  id: '',
  title: '',
  createdAt: firebase.firestore.Timestamp.fromMillis(Date.now()),
  updatedAt: firebase.firestore.Timestamp.fromMillis(Date.now()),
  createdBy: {
    id: '',
    firstName: '',
    lastName: '',
  },
  isTeamEmailSignature: false,
  userId: '',
  personalDetails: {
    firstName: '',
    lastName: '',
    jobTitle: '',
    company: '',
    headline: '',
  },
  contactDetails: {},
  contactDetailsOrder: [],
  socialLinks: {},
  socialLinksOrder: [],
  design: initialEmailSignatureDesignConfig,
  images: [],
  saveContact: {
    cardId: '',
    buttonColorUseThemeColor: true,
    buttonColor: '#f45a57',
    displaySaveContact: true,
  },
};

type UpdateContactDetailsPackage = {
  fieldId: EmailSignatureContactDetailsFieldId;
  updatedFieldValues: EmailSignatureContactDetailsField;
};

type UpdateContactDetailsStringFieldPackage = {
  fieldId: EmailSignatureContactDetailsFieldId;
  updatedFieldValue: string;
};

type UpdateSocialLinksStringFieldPackage = {
  fieldId: EmailSignatureSocialLinkId;
  updatedFieldValue: string;
};

type UpdateContactDetailsIconPackage = {
  fieldId: EmailSignatureContactDetailsFieldId;
  updatedFieldValue: EmailSignatureIconName;
};

type UpdateContactDetailsFieldWidthPackage = {
  fieldId: EmailSignatureContactDetailsFieldId;
  updatedFieldValue: EmailSignatureContactDetailsFieldWidth;
};

type UpdateContactDetailsFieldPhoneNumberPackage = {
  fieldId: EmailSignatureContactDetailsFieldId;
  updatedFieldValue: string;
  countryCode?: string;
};

type ContactDetailsSwapFieldsPackage = {
  fieldIdA: EmailSignatureContactDetailsFieldId;
  fieldIdB: EmailSignatureContactDetailsFieldId;
};

type SocialLinksSwapFieldsPackage = {
  fieldIdA: EmailSignatureSocialLinkId;
  fieldIdB: EmailSignatureSocialLinkId;
};

type UpdateDesignDisplayIconInContactDetails = {
  displayIcon: boolean;
  changeFieldWidth?: boolean;
};

type UpdateAssignedProfile = {
  email: string;
  orgId: string;
};

type OwnerOfEmailSignaturePackage = {
  userId: string;
  orgId: string | undefined;
  isTeamEmailSignature: boolean;
};

type DetailsFromProfiles = {
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
};

export type EmailSignaturesActions =
  | { type: 'RESET_EMAIL_SIGNATURE' }
  | { type: 'CHANGE_OWNER_OF_EMAIL_SIGNATURE'; value: OwnerOfEmailSignaturePackage }
  | { type: 'IMPORT_EMAIL_SIGNATURE'; value: EmailSignatureV2 }
  | { type: 'UPDATE_SAVE_CONTACT_VISIBLE'; value: boolean }
  | { type: 'UPDATE_SAVE_BUTTON_COLOR_USE_THEME'; value: boolean }
  | { type: 'UPDATE_SAVE_BUTTON_COLOR'; value: string }
  | { type: 'IMPORT_DATA_FROM_CARD'; value: Card }
  | { type: 'UPDATE_SIGNATURE_TITLE'; value: string }
  | { type: 'UPDATE_SIGNATURE_RECIPIENT_EMAIL'; value: string }
  | { type: 'UPDATE_ASSIGNED_PROFILE'; value: UpdateAssignedProfile }
  | { type: 'UNASSIGN_PROFILE' }
  | { type: 'UPDATE_FIRST_NAME'; value: string }
  | { type: 'UPDATE_LAST_NAME'; value: string }
  | { type: 'UPDATE_JOB_TITLE'; value: string }
  | { type: 'UPDATE_COMPANY'; value: string }
  | { type: 'UPDATE_QUOTE'; value: string }
  | { type: 'ADD_IMAGE'; value: EmailSignatureImage }
  | { type: 'CHANGE_IMAGE_ORIGINAL_IMAGE'; value: EmailSignatureImageFieldId }
  | { type: 'DELETE_IMAGE'; value: EmailSignatureImageFieldId }
  | {
      type: 'UPDATE_IMAGE_URL';
      value: { imageUrl: string; id: EmailSignatureImageFieldId; crop: EmailSignatureCrop | null };
    }
  | { type: 'CLEAR_IMAGE_URL'; value: { id: EmailSignatureImageFieldId } }
  | {
      type: 'UPDATE_IMAGE_CROP';
      value: { id: EmailSignatureImageFieldId; crop: EmailSignatureCrop | null };
    }
  | { type: 'UPDATE_IMAGE_EXTERNAL_LINK'; value: { externalLink: string; id: EmailSignatureImageFieldId } }
  | { type: 'UPDATE_IMAGE_SHAPE'; value: { id: EmailSignatureImageFieldId; shape: EmailSignatureShapes } }
  | { type: 'UPDATE_IMAGE_SIZE'; value: { id: EmailSignatureImageFieldId; size: EmailSignatureSizes } }
  | { type: 'MAKE_IMAGE_PRIMARY'; value: EmailSignatureImageFieldId }
  | { type: 'MAKE_IMAGE_SECONDARY'; value: EmailSignatureImageFieldId }
  | { type: 'DELETE_ALL_IMAGES' }
  | { type: 'ADD_CONTACT_DETAIL_FIELD'; value: EmailSignatureContactDetailsField }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD_LABEL'; value: UpdateContactDetailsStringFieldPackage }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD_TITLE'; value: UpdateContactDetailsStringFieldPackage }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD_PHONE_NUMBER'; value: UpdateContactDetailsFieldPhoneNumberPackage }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD_EXTENSION'; value: UpdateContactDetailsStringFieldPackage }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD_VALUE'; value: UpdateContactDetailsStringFieldPackage }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD_ICON'; value: UpdateContactDetailsIconPackage }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD_ICON_URL'; value: UpdateContactDetailsStringFieldPackage }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD_WIDTH'; value: UpdateContactDetailsFieldWidthPackage }
  | { type: 'UPDATE_CONTACT_DETAIL_FIELD'; value: UpdateContactDetailsPackage }
  | { type: 'SWAP_CONTACT_DETAIL_FIELDs'; value: ContactDetailsSwapFieldsPackage }
  | { type: 'DELETE_CONTACT_DETAIL_FIELD'; value: EmailSignatureContactDetailsFieldId }
  | { type: 'ADD_SOCIAL_LINK'; value: EmailSignatureSocialLink }
  | { type: 'UPDATE_SOCIAL_LINK_VALUE'; value: UpdateSocialLinksStringFieldPackage }
  | { type: 'SWAP_SOCIAL_LINKS'; value: SocialLinksSwapFieldsPackage }
  | { type: 'DELETE_SOCIAL_LINK'; value: EmailSignatureSocialLinkId }
  | { type: 'ADD_LEGAL_DISCLAIMER'; value: EmailSignatureLegalDisclaimer }
  | { type: 'DELETE_LEGAL_DISCLAIMER' }
  | { type: 'UPDATE_LEGAL_DISCLAIMER_CONTENT'; value: string }
  | { type: 'UPDATE_LEGAL_DISCLAIMER_FONT_SIZE'; value: EmailSignatureSizes }
  | { type: 'ADD_ENVIRONMENTAL_FOOTER'; value: EmailSignatureEnvironmentalFooter }
  | { type: 'DELETE_ENVIRONMENTAL_FOOTER' }
  | { type: 'UPDATE_ENVIRONMENTAL_FOOTER_CONTENT'; value: string }
  | { type: 'UPDATE_ENVIRONMENTAL_FOOTER_ICON'; value: EmailSignatureIconName }
  | { type: 'UPDATE_ENVIRONMENTAL_FOOTER_ICON_URL'; value: string }
  | { type: 'ADD_CUSTOM_BANNER'; value: { id: EmailSignatureBannerId; imageUrl: string } }
  | { type: 'CHANGE_CUSTOM_BANNER_ORIGINAL_IMAGE' }
  | { type: 'DELETE_CUSTOM_BANNER' }
  | {
      type: 'UPDATE_CUSTOM_BANNER_IMAGE_URL';
      value: { imageReference: string; crop: EmailSignatureCrop | null };
    }
  | { type: 'UPDATE_CUSTOM_BANNER_EXTERNAL_LINK'; value: string }
  | { type: 'UPDATE_CUSTOM_BANNER_SIZE'; value: EmailSignatureSizes }
  | { type: 'UPDATE_DESIGN_FONT'; value: EmailSignatureFonts }
  | { type: 'UPDATE_DESIGN_THEME_COLOR'; value: string }
  | { type: 'UPDATE_DESIGN_ICON_IN_CONTACT'; value: UpdateDesignDisplayIconInContactDetails }
  | { type: 'UPDATE_DESIGN_CONTACT_ICON_USE_THEME'; value: boolean }
  | { type: 'UPDATE_DESIGN_CONTACT_ICON_COLOR'; value: string }
  | { type: 'UPDATE_DESIGN_SOCIAL_FILL_STYLE'; value: EmailSignatureFillStyle }
  | { type: 'UPDATE_DESIGN_SOCIAL_SHAPE'; value: EmailSignatureShapes }
  | { type: 'UPDATE_DESIGN_SOCIAL_SIZE'; value: EmailSignatureSizes }
  | { type: 'UPDATE_DESIGN_SOCIAL_COLOR_MODE'; value: EmailSignatureSocialColors }
  | { type: 'UPDATE_DESIGN_SOCIAL_COLOR'; value: string }
  | { type: 'UPDATE_SOCIAL_LINK_ICON_URL'; value: UpdateSocialLinksStringFieldPackage }
  | { type: 'UPDATE_PREVENT_EDITING'; value: boolean }
  | {
      type: 'FILL_DETAILS_FROM_PROFILE';
      value: DetailsFromProfiles;
    }
  | { type: 'UPDATE_TRACKING_PIXEL'; value: TrackingPixel };

function emailSignatureFieldsReducerFunc(draft: Draft<EmailSignatureV2>, action: EmailSignaturesActions) {
  switch (action.type) {
    case 'RESET_EMAIL_SIGNATURE': {
      return initialEmailSignatureContextValues;
    }
    case 'CHANGE_OWNER_OF_EMAIL_SIGNATURE':
      draft.userId = action.value.userId;
      draft.orgId = action.value.orgId;
      draft.isTeamEmailSignature = action.value.isTeamEmailSignature;
      break;
    case 'IMPORT_EMAIL_SIGNATURE':
      return action.value;
    case 'IMPORT_DATA_FROM_CARD': {
      const currentEmailSignature = current(draft);
      const personalDetails = extractEmailSignaturePersonalDetailsFromCard(action.value);
      const [contactDetails, contactDetailsOrder] = extractEmailSignatureContactDetailsFieldsFromCard(
        action.value,
        currentEmailSignature.design
      );
      const [socialLinks, socialLinksOrder] = extractEmailSignatureSocialLinksFromCard(action.value);
      draft.personalDetails = personalDetails;
      draft.contactDetails = contactDetails;
      draft.contactDetailsOrder = contactDetailsOrder;
      draft.socialLinks = socialLinks;
      draft.socialLinksOrder = socialLinksOrder;
      draft.design.themeColor = action.value.color ?? '#f45a57';
      draft.importedCardId = action.value.cardId;

      break;
    }
    case 'UPDATE_SAVE_CONTACT_VISIBLE':
      draft.saveContact.displaySaveContact = action.value;
      break;
    case 'UPDATE_SAVE_BUTTON_COLOR_USE_THEME': {
      const currentEmailSignature = current(draft);
      if (!draft.saveContact) return;
      draft.saveContact.buttonColorUseThemeColor = action.value;
      if (action.value) {
        draft.saveContact.buttonColor = currentEmailSignature.design.themeColor;
      }
      break;
    }
    case 'UPDATE_SAVE_BUTTON_COLOR':
      draft.saveContact.buttonColor = action.value;
      break;
    case 'UPDATE_SIGNATURE_TITLE':
      draft.title = action.value;
      break;
    case 'UPDATE_SIGNATURE_RECIPIENT_EMAIL':
      draft.recipientEmail = action.value;
      break;
    case 'UPDATE_FIRST_NAME':
      draft.personalDetails.firstName = action.value;
      break;
    case 'UPDATE_LAST_NAME':
      draft.personalDetails.lastName = action.value;
      break;
    case 'UPDATE_JOB_TITLE':
      draft.personalDetails.jobTitle = action.value;
      break;
    case 'UPDATE_COMPANY':
      draft.personalDetails.company = action.value;
      break;
    case 'UPDATE_QUOTE':
      draft.personalDetails.headline = action.value;
      break;
    case 'ADD_CONTACT_DETAIL_FIELD': {
      const currentEmailSignature = current(draft);
      const nonEmptyContactDetailsOrder = currentEmailSignature.contactDetailsOrder.filter(
        (contactDetailFieldId) => {
          return currentEmailSignature.contactDetails[contactDetailFieldId].value;
        }
      );
      const nonEmptyContactDetailFields = pickBy(
        currentEmailSignature.contactDetails,
        (contactDetailField) => !!contactDetailField.value
      );
      draft.contactDetails = { ...nonEmptyContactDetailFields };
      draft.contactDetailsOrder = [...nonEmptyContactDetailsOrder];
      draft.contactDetails[action.value.id] = action.value;
      draft.contactDetailsOrder.push(action.value.id);
      break;
    }
    case 'UPDATE_CONTACT_DETAIL_FIELD_LABEL':
      if (!draft.contactDetails[action.value.fieldId]) return;

      draft.contactDetails[action.value.fieldId].label = action.value.updatedFieldValue;
      break;
    case 'UPDATE_CONTACT_DETAIL_FIELD_TITLE':
      if (!draft.contactDetails[action.value.fieldId]) return;

      draft.contactDetails[action.value.fieldId].title = action.value.updatedFieldValue;
      break;
    case 'UPDATE_CONTACT_DETAIL_FIELD_PHONE_NUMBER':
      if (!draft.contactDetails[action.value.fieldId]) return;

      draft.contactDetails[action.value.fieldId].value = action.value.updatedFieldValue;
      draft.contactDetails[action.value.fieldId].countryCode = action.value.countryCode;
      break;
    case 'UPDATE_CONTACT_DETAIL_FIELD_VALUE': {
      if (!draft.contactDetails[action.value.fieldId]) return;

      draft.contactDetails[action.value.fieldId].value = action.value.updatedFieldValue;
      break;
    }
    case 'UPDATE_CONTACT_DETAIL_FIELD_ICON':
      if (!draft.contactDetails[action.value.fieldId]) return;

      draft.contactDetails[action.value.fieldId].baseIcon = action.value.updatedFieldValue;
      break;
    case 'UPDATE_CONTACT_DETAIL_FIELD_ICON_URL':
      if (!draft.contactDetails[action.value.fieldId]) return;

      draft.contactDetails[action.value.fieldId].iconUrl = action.value.updatedFieldValue;
      break;
    case 'UPDATE_CONTACT_DETAIL_FIELD_EXTENSION':
      if (!draft.contactDetails[action.value.fieldId]) return;

      draft.contactDetails[action.value.fieldId].extension = action.value.updatedFieldValue;
      break;
    case 'UPDATE_CONTACT_DETAIL_FIELD_WIDTH':
      if (!draft.contactDetails[action.value.fieldId]) return;

      draft.contactDetails[action.value.fieldId].fieldWidth = action.value.updatedFieldValue;
      break;
    case 'UPDATE_CONTACT_DETAIL_FIELD':
      if (!draft.contactDetails[action.value.fieldId]) return;
      draft.contactDetails[action.value.fieldId] = action.value.updatedFieldValues;
      break;
    case 'SWAP_CONTACT_DETAIL_FIELDs': {
      const oldIndexA = draft.contactDetailsOrder.findIndex((fieldId) => fieldId === action.value.fieldIdA);
      const oldIndexB = draft.contactDetailsOrder.findIndex((fieldId) => fieldId === action.value.fieldIdB);
      draft.contactDetailsOrder[oldIndexA] = action.value.fieldIdB;
      draft.contactDetailsOrder[oldIndexB] = action.value.fieldIdA;
      break;
    }
    case 'DELETE_CONTACT_DETAIL_FIELD': {
      delete draft.contactDetails[action.value];
      const indexOfFieldToDelete = draft.contactDetailsOrder.findIndex((fieldId) => fieldId === action.value);
      if (indexOfFieldToDelete !== -1) draft.contactDetailsOrder.splice(indexOfFieldToDelete, 1);
      break;
    }
    case 'ADD_SOCIAL_LINK':
      draft.socialLinks[action.value.id] = action.value;
      draft.socialLinksOrder.push(action.value.id);
      break;
    case 'UPDATE_SOCIAL_LINK_VALUE':
      if (!draft.socialLinks[action.value.fieldId]) return;
      draft.socialLinks[action.value.fieldId].value = action.value.updatedFieldValue;
      break;
    case 'SWAP_SOCIAL_LINKS': {
      const oldIndexA = draft.socialLinksOrder.findIndex((fieldId) => fieldId === action.value.fieldIdA);
      const oldIndexB = draft.socialLinksOrder.findIndex((fieldId) => fieldId === action.value.fieldIdB);
      draft.socialLinksOrder[oldIndexA] = action.value.fieldIdB;
      draft.socialLinksOrder[oldIndexB] = action.value.fieldIdA;
      break;
    }
    case 'DELETE_SOCIAL_LINK': {
      delete draft.socialLinks[action.value];
      const indexOfFieldToDelete = draft.socialLinksOrder.findIndex((fieldId) => fieldId === action.value);
      if (indexOfFieldToDelete !== -1) draft.socialLinksOrder.splice(indexOfFieldToDelete, 1);
      break;
    }
    case 'ADD_LEGAL_DISCLAIMER':
      draft.legalDisclaimer = action.value;
      break;
    case 'DELETE_LEGAL_DISCLAIMER':
      delete draft.legalDisclaimer;
      break;
    case 'UPDATE_LEGAL_DISCLAIMER_CONTENT':
      if (!draft.legalDisclaimer) {
        return;
      }
      draft.legalDisclaimer.value = action.value;
      break;
    case 'UPDATE_LEGAL_DISCLAIMER_FONT_SIZE':
      if (!draft.legalDisclaimer) {
        return;
      }
      draft.legalDisclaimer.fontSize = action.value;
      break;
    case 'ADD_ENVIRONMENTAL_FOOTER':
      draft.environmentalFooter = action.value;
      break;
    case 'DELETE_ENVIRONMENTAL_FOOTER':
      delete draft.environmentalFooter;
      break;
    case 'UPDATE_ENVIRONMENTAL_FOOTER_CONTENT':
      if (!draft.environmentalFooter) {
        return;
      }
      draft.environmentalFooter.value = action.value;
      break;
    case 'UPDATE_ENVIRONMENTAL_FOOTER_ICON':
      if (!draft.environmentalFooter) {
        return;
      }
      draft.environmentalFooter.baseIcon = action.value;
      break;
    case 'UPDATE_ENVIRONMENTAL_FOOTER_ICON_URL':
      if (!draft.environmentalFooter) {
        return;
      }
      draft.environmentalFooter.iconUrl = action.value;
      break;
    case 'ADD_CUSTOM_BANNER':
      if (draft.banner) {
        draft.banner.imageUrl = action.value.imageUrl;
      } else {
        draft.banner = {
          id: action.value.id,
          externalLink: '',
          imageUrl: action.value.imageUrl,
          size: EmailSignatureSizes.LARGE,
          crop: null,
          originalImageUrl: '',
        };
      }
      break;
    case 'CHANGE_CUSTOM_BANNER_ORIGINAL_IMAGE':
      if (draft.banner) {
        draft.banner.imageUrl = '';
        draft.banner.crop = null;
      }
      break;
    case 'DELETE_CUSTOM_BANNER':
      delete draft.banner;
      break;
    case 'UPDATE_CUSTOM_BANNER_IMAGE_URL':
      if (!draft.banner) {
        return;
      }
      draft.banner.imageUrl = action.value.imageReference;
      draft.banner.crop = action.value.crop;
      break;
    case 'UPDATE_CUSTOM_BANNER_EXTERNAL_LINK':
      if (!draft.banner) {
        return;
      }
      draft.banner.externalLink = action.value;
      break;
    case 'UPDATE_CUSTOM_BANNER_SIZE':
      if (!draft.banner) {
        return;
      }
      draft.banner.size = action.value;
      break;
    case 'UPDATE_DESIGN_FONT':
      draft.design.font = action.value;
      break;
    case 'UPDATE_DESIGN_THEME_COLOR':
      draft.design.themeColor = action.value;
      break;
    case 'UPDATE_DESIGN_ICON_IN_CONTACT': {
      const currentEmailSignature = current(draft);
      draft.design.contactDetailsDisplayIcon = action.value.displayIcon;
      if (action.value.changeFieldWidth) {
        const contactFieldsIds = currentEmailSignature.contactDetailsOrder;
        if (!action.value.displayIcon)
          contactFieldsIds.forEach(
            (contactFieldId) =>
              (draft.contactDetails[contactFieldId].fieldWidth =
                EmailSignatureContactDetailsFieldWidth.FULL_WIDTH)
          );
        else {
          contactFieldsIds.forEach((contactFieldId) => {
            draft.contactDetails[contactFieldId].fieldWidth = contactDetailsFieldExceedsLimit(
              currentEmailSignature.contactDetails[contactFieldId].value,
              currentEmailSignature.design.font
            )
              ? EmailSignatureContactDetailsFieldWidth.FULL_WIDTH
              : EmailSignatureContactDetailsFieldWidth.HALF_WIDTH;
          });
        }
      }
      break;
    }
    case 'UPDATE_DESIGN_CONTACT_ICON_USE_THEME':
      draft.design.contactDetailsIconUseThemeColor = action.value;
      break;
    case 'UPDATE_DESIGN_CONTACT_ICON_COLOR':
      draft.design.contactDetailsIconColor = action.value;
      break;
    case 'UPDATE_DESIGN_SOCIAL_FILL_STYLE':
      draft.design.socialLinksFillStyle = action.value;
      break;
    case 'UPDATE_DESIGN_SOCIAL_SHAPE':
      draft.design.socialLinksShape = action.value;
      break;
    case 'UPDATE_DESIGN_SOCIAL_SIZE':
      draft.design.socialLinksSize = action.value;
      break;
    case 'UPDATE_DESIGN_SOCIAL_COLOR_MODE':
      draft.design.socialLinksColorMode = action.value;
      break;
    case 'UPDATE_DESIGN_SOCIAL_COLOR':
      draft.design.socialLinksColor = action.value;
      break;
    case 'UPDATE_SOCIAL_LINK_ICON_URL':
      if (!draft.socialLinks[action.value.fieldId]) {
        return;
      }
      draft.socialLinks[action.value.fieldId].iconUrl = action.value.updatedFieldValue;
      break;
    case 'ADD_IMAGE': {
      const currentEmailSignature = current(draft);
      draft.images.push({
        ...action.value,
        // there should always be one primary image, however due to a bug in setup process there could be instances where 0 image is primary so
        // we self fix this bug by adding this extra check.
        isPrimary:
          currentEmailSignature.images.length === 0 ? true : !currentEmailSignature.images[0].isPrimary,
      });
      break;
    }
    case 'CHANGE_IMAGE_ORIGINAL_IMAGE': {
      const indexOfImageToChange = draft.images.findIndex((image) => image.id === action.value);

      draft.images[indexOfImageToChange].imageUrl = '';
      break;
    }
    case 'DELETE_IMAGE': {
      const currentEmailSignature = current(draft);
      const indexOfImageToChange = draft.images.findIndex((image) => image.id === action.value);
      const indexOfOtherImage = draft.images.findIndex((image) => image.id !== action.value);
      if (indexOfOtherImage !== -1 && currentEmailSignature.images[indexOfImageToChange].isPrimary) {
        draft.images[indexOfOtherImage].isPrimary = true;
      }
      draft.images.splice(indexOfImageToChange, 1);
      break;
    }
    case 'UPDATE_IMAGE_URL': {
      const indexOfImageToChange = draft.images.findIndex((image) => image.id === action.value.id);
      draft.images[indexOfImageToChange].imageUrl = action.value.imageUrl;
      draft.images[indexOfImageToChange].crop = action.value.crop;
      break;
    }
    case 'CLEAR_IMAGE_URL': {
      // This block runs when the user "Replaces" the image, when this happens the image ID needs to change
      // in order to invalidate places this image is cached (Cloudflare caches the image). This code deletes the image
      // and adds it back with a new ID in the same position in the images array.
      const indexOfImageToChange = draft.images.findIndex((image) => image.id === action.value.id);
      const imageToReplace = draft.images[indexOfImageToChange];
      const newImage = {
        id: generateNewImageId(),
        externalLink: imageToReplace.externalLink,
        imageUrl: '',
        shape: imageToReplace.shape,
        size: imageToReplace.size,
        type: imageToReplace.type,
        isPrimary: imageToReplace.isPrimary,
        originalImageUrl: '',
        crop: null,
      };
      draft.images.splice(indexOfImageToChange, 1, newImage);
      break;
    }
    case 'UPDATE_IMAGE_CROP': {
      const indexOfImageToChange = draft.images.findIndex((image) => image.id === action.value.id);
      draft.images[indexOfImageToChange].crop = action.value.crop;
      break;
    }
    case 'UPDATE_IMAGE_EXTERNAL_LINK': {
      const indexOfImageToChange = draft.images.findIndex((image) => image.id === action.value.id);

      draft.images[indexOfImageToChange].externalLink = action.value.externalLink;
      break;
    }
    case 'UPDATE_IMAGE_SHAPE': {
      const indexOfImageToChange = draft.images.findIndex((image) => image.id === action.value.id);

      draft.images[indexOfImageToChange].shape = action.value.shape;
      break;
    }
    case 'UPDATE_IMAGE_SIZE': {
      const indexOfImageToChange = draft.images.findIndex((image) => image.id === action.value.id);

      draft.images[indexOfImageToChange].size = action.value.size;
      break;
    }
    case 'MAKE_IMAGE_PRIMARY': {
      const indexOfPreviousPrimary = draft.images.findIndex((image) => image.isPrimary);
      const indexOfPrimaryToBe = draft.images.findIndex((image) => image.id === action.value);
      if (indexOfPrimaryToBe === -1) return;

      draft.images[indexOfPreviousPrimary].isPrimary = false;
      draft.images[indexOfPrimaryToBe].isPrimary = true;
      break;
    }

    case 'MAKE_IMAGE_SECONDARY': {
      const indexOfPreviousSecondary = draft.images.findIndex((image) => !image.isPrimary);
      const indexOfSecondaryToBe = draft.images.findIndex((image) => image.id === action.value);
      if (indexOfSecondaryToBe === -1) return;

      draft.images[indexOfPreviousSecondary].isPrimary = true;
      draft.images[indexOfSecondaryToBe].isPrimary = false;
      break;
    }
    case 'UPDATE_ASSIGNED_PROFILE': {
      draft.recipientEmail = action.value.email;
      draft.orgId = action.value.orgId;

      break;
    }
    case 'UNASSIGN_PROFILE': {
      draft.recipientEmail = '';
      draft.contactDetails = {};
      draft.contactDetailsOrder = [];
      draft.socialLinks = {};
      draft.socialLinksOrder = [];
      draft.personalDetails.firstName = '';
      draft.personalDetails.lastName = '';
      draft.personalDetails.jobTitle = '';
      draft.images = [];

      break;
    }
    case 'DELETE_ALL_IMAGES':
      draft.images = [];
      break;
    case 'UPDATE_PREVENT_EDITING':
      draft.preventEditingSignature = action.value;
      break;
    case 'FILL_DETAILS_FROM_PROFILE': {
      const currentEmailSignature = current(draft);

      draft.personalDetails.firstName = action.value.firstName;
      draft.personalDetails.lastName = action.value.lastName;
      draft.personalDetails.jobTitle = action.value.jobTitle;
      draft.personalDetails.headline = '';

      const newEmailFieldId = generateNewEmailSignatureContactFieldId();
      const contactFieldWidth = measureTextWidth(action.value.email, currentEmailSignature.design.font);
      draft.contactDetails = {
        [newEmailFieldId]: {
          id: newEmailFieldId,
          label: '',
          type: EmailSignatureContactDetailsFieldTypes.EMAIL,
          value: action.value.email,
          baseIcon: 'envelope',
          fieldWidth: deriveFieldWidth(contactFieldWidth),
          iconUrl: '',
        },
      };
      draft.contactDetailsOrder = [newEmailFieldId];

      draft.socialLinks = {};
      draft.socialLinksOrder = [];
      break;
    }
    case 'UPDATE_TRACKING_PIXEL': {
      draft.trackingPixel = action.value;
      break;
    }
    default:
      break;
  }
}

export const emailSignatureFieldsReducer = produce(emailSignatureFieldsReducerFunc);
